// F8 — the "request view" body of the shared review slide-out. When a
// party-surface Review Queue row is a pending-recipient package (nothing
// submitted yet, so no StagedSubmissionReviewPanel to show), staff still need
// to confirm WHAT was sent and where each item stands. This renders the
// package summary (recipient / sent / due), the requested-document list (name
// + kind + per-item status, identical to the package-detail page), and the
// same quick actions available elsewhere — edit due date and send a reminder.
// The submitted-item review path is unchanged; this is a second, additive
// mode of StagedSubmissionReviewDrawer.
//
// WS-F — the state-gated Archive / soft-Delete controls (per-item + whole
// package) that previously landed ONLY on CollectionPackageDetailPage (reached
// via Portal Accounts admin) are mirrored here so staff working the Review
// Queue tab have the same control set without leaving the entity. The write
// endpoints are server-gated by CanArchiveOrDeleteAsync (permission OR
// account-assignee), so the controls are shown unconditionally and errors
// surface as toasts — matching the already-ungated "Archive package" button in
// StagedSubmissionReviewPanel (the sibling body of this same drawer).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Archive, Send, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  OverflowMenu,
  type OverflowMenuItem,
} from "@/design-system/components/ui";
import { displayEmail } from "@/design-system/lib/format";
import { useToast } from "@/components/ui/Toast";
import { useDateFormatter } from "@/utils/datetime";
import { extractErrorMessage } from "@/api/client";
import { PackageDueDateEditor } from "@/components/Collection/PackageDueDateEditor";
import { ItemStatusBadge } from "@/components/Collection/ItemStatusBadge";
import {
  collectionPackagesApi,
  type CollectionPackageQueueItem,
} from "@/api/collectionPackages";
import { portalAccountsApi } from "@/api/portalAccounts";
import { collectionStagingKeys } from "@/api/collectionStagingKeys";
import { collectionPackagesKeys } from "@/hooks/collection/collectionPackagesKeys";
import { portalAccountsKeys } from "@/hooks/portalAccounts/portalAccountsKeys";
import { portalAbsorptionKeys } from "@/hooks/portalAbsorption/portalAbsorptionKeys";

interface PackageRequestViewProps {
  /** The portal account the request view is scoped to (server IDOR-scopes
   *  the item read to this account). */
  accountId: string;
  /** The clicked pending-recipient queue row — carries the summary facts
   *  (recipient, sent, due, status, item counts). */
  pkg: CollectionPackageQueueItem;
  /** WS-F — invoked after the WHOLE package is archived or soft-deleted so the
   *  host drawer closes; the package then leaves the active Review Queue (the
   *  invalidation refreshes the list behind it). */
  onPackageGone?: () => void;
}

export function PackageRequestView({
  accountId,
  pkg,
  onPackageGone,
}: PackageRequestViewProps) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const { date } = useDateFormatter();

  // WS-F — "Show archived" is URL-persisted and parsed at RENDER time (no
  // post-mount effect — the page-double-fetch rule; mirrors
  // CollectionPackageDetailPage's toggle). When on, the read includes archived
  // items so each can be Unarchived; soft-deleted items never render.
  const [params, setParams] = useSearchParams();
  const showArchived = params.get("reqArchived") === "1";
  const setShowArchived = (next: boolean) => {
    const p = new URLSearchParams(params);
    if (next) p.set("reqArchived", "1");
    else p.delete("reqArchived");
    setParams(p, { replace: true });
  };

  const items = useQuery({
    queryKey: portalAbsorptionKeys.accountPackageItems(accountId, pkg.id, showArchived),
    queryFn: () => portalAccountsApi.listPackageItems(accountId, pkg.id, showArchived),
    enabled: Boolean(accountId),
  });

  const rows = items.data?.items ?? [];
  // WS-F — package soft-Delete is offered only when EVERY item (incl. hidden
  // archived ones) is untouched; the server computes this authoritatively.
  const canDeletePackage = items.data?.canDeletePackage ?? false;

  // WS-F — every Archive / Unarchive / soft-Delete refresh: the drawer's item
  // read (both includeArchived variants) + the account's package list /
  // progress roll-up ride the "portal-account-packages" prefix; the Review
  // Queue tab's staging list + submission summary ride "collection-staging";
  // the account-summary + registry reads mirror CollectionPackageDetailPage.
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.accountPackagesPrefix });
    void qc.invalidateQueries({ queryKey: collectionStagingKeys.all });
    void qc.invalidateQueries({ queryKey: collectionPackagesKeys.portalAccountSummaryPrefix });
    void qc.invalidateQueries({ queryKey: portalAccountsKeys.all() });
  };

  // Mirrors StagedSubmissionReviewPanel's remind gate — Draft packages reach
  // these rows (ListOutstanding excludes only Completed + Cancelled), and a
  // reminder is only meaningful for a sent, still-open package.
  const remindBlockedReason =
    pkg.status === "Draft"
      ? "Package not yet sent"
      : pkg.status === "Completed"
        ? "Package already completed"
        : pkg.status === "Cancelled" || pkg.status === "Archived"
          ? `Package ${pkg.status.toLowerCase()}`
          : null;

  const remind = useMutation({
    mutationFn: () => collectionPackagesApi.remind(pkg.id),
    onSuccess: () => {
      success(`Reminder sent to ${pkg.recipientFullName}.`);
      void qc.invalidateQueries({ queryKey: collectionStagingKeys.all });
      void qc.invalidateQueries({ queryKey: portalAbsorptionKeys.accountPackagesPrefix });
    },
    onError: (e: Error) => error(extractErrorMessage(e)),
  });

  // WS-F — per-item Archive / Unarchive / soft-Delete. The legacy Draft-only
  // hard delete is replaced by soft-Delete in the OverflowMenu; a touched item
  // is Archive-only (canHardDelete drives which is offered).
  const archiveItem = useMutation({
    mutationFn: (itemId: string) => collectionPackagesApi.archiveItem(pkg.id, itemId),
    onSuccess: () => {
      success("Item archived.");
      invalidate();
    },
    onError: (e: Error) => error(extractErrorMessage(e)),
  });
  const unarchiveItem = useMutation({
    mutationFn: (itemId: string) => collectionPackagesApi.unarchiveItem(pkg.id, itemId),
    onSuccess: () => {
      success("Item restored.");
      invalidate();
    },
    onError: (e: Error) => error(extractErrorMessage(e)),
  });
  const deleteItem = useMutation({
    mutationFn: (itemId: string) => collectionPackagesApi.deleteItem(pkg.id, itemId),
    onSuccess: () => {
      success("Item deleted.");
      invalidate();
    },
    onError: (e: Error) => error(extractErrorMessage(e)),
  });

  // WS-F — whole-package Archive (recoverable) + soft-Delete (only when every
  // item is untouched; the server is authoritative and 400s otherwise, surfaced
  // as a toast). Both close the drawer on success — the package leaves the
  // active Review Queue.
  const archivePackage = useMutation({
    mutationFn: () => collectionPackagesApi.archive(pkg.id),
    onSuccess: () => {
      success("Package archived.");
      invalidate();
      onPackageGone?.();
    },
    onError: (e: Error) => error(extractErrorMessage(e)),
  });
  const deletePackage = useMutation({
    mutationFn: () => collectionPackagesApi.deletePackage(pkg.id),
    onSuccess: () => {
      success("Package deleted.");
      invalidate();
      onPackageGone?.();
    },
    onError: (e: Error) => error(extractErrorMessage(e)),
  });

  return (
    <div className="flex flex-col gap-space-4" data-testid="package-request-view">
      {/* Package summary. Due date stays inline-editable (same control as the
          submission-review header). */}
      <section className="rounded-md border border-border-light bg-white-pure p-space-4 grid grid-cols-2 md:grid-cols-4 gap-space-3 text-small">
        <div className="flex flex-col">
          <span className="text-caption uppercase tracking-wide text-gray-mid">Recipient</span>
          <span className="text-onyx mt-space-1">
            {pkg.recipientFullName}
            {pkg.recipientRoleLabel ? ` · ${pkg.recipientRoleLabel}` : ""}
            <span className="block text-caption text-gray-mid">
              {displayEmail(pkg.recipientEmail)}
            </span>
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-caption uppercase tracking-wide text-gray-mid">Sent</span>
          <span className="text-onyx mt-space-1">{pkg.sentAt ? date(pkg.sentAt) : "—"}</span>
        </div>
        <div className="flex flex-col">
          <PackageDueDateEditor packageId={pkg.id} dueDate={pkg.dueDate} />
        </div>
        <div className="flex flex-col">
          <span className="text-caption uppercase tracking-wide text-gray-mid">Progress</span>
          <span className="text-onyx mt-space-1">
            {pkg.itemsCompleted}/{pkg.itemsTotal} completed
          </span>
        </div>
      </section>

      {/* Quick actions — reminder (status-gated) + WS-F whole-package Archive /
          soft-Delete. Edit-due-date lives in the summary via PackageDueDateEditor. */}
      <div className="flex flex-wrap items-center justify-end gap-space-2">
        <Button
          type="button"
          variant="secondary"
          disabled={remind.isPending || remindBlockedReason !== null}
          loading={remind.isPending}
          onClick={() => remind.mutate()}
          title={remindBlockedReason ?? "Send a reminder email to the recipient"}
        >
          <Send size={14} className="mr-1 inline" /> Send reminder
        </Button>
        {pkg.status !== "Archived" && (
          <Button
            type="button"
            variant="secondary"
            disabled={archivePackage.isPending}
            loading={archivePackage.isPending}
            onClick={() => {
              if (confirm("Archive this package? It will be hidden from active queues.")) {
                archivePackage.mutate();
              }
            }}
            title="Move this request to the archive bucket"
          >
            <Archive size={14} className="mr-1 inline" /> Archive package
          </Button>
        )}
        {/* WS-F — soft-Delete (recoverable). Offered only when every item is
            untouched (server-authoritative canDeletePackage); hidden otherwise
            (touched → Archive-only). */}
        {canDeletePackage && (
          <Button
            type="button"
            variant="destructive"
            disabled={deletePackage.isPending}
            loading={deletePackage.isPending}
            onClick={() => {
              if (
                confirm(
                  "Delete this package? It will be removed from all views but can be recovered by an admin.",
                )
              ) {
                deletePackage.mutate();
              }
            }}
            title="Delete this request (all items untouched)"
          >
            <Trash2 size={14} className="mr-1 inline" /> Delete package
          </Button>
        )}
      </div>

      {/* Requested documents — what was actually sent + where each stands. */}
      <section className="rounded-md border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3">
        <header className="flex items-center justify-between gap-space-3">
          <h3 className="text-small font-semibold text-onyx m-0">Requested documents</h3>
          <div className="flex items-center gap-space-3">
            {/* WS-F — URL-persisted "Show archived" toggle (parsed at render). */}
            <label className="inline-flex items-center gap-space-2 text-caption text-gray-mid">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
            {!items.isLoading && !items.isError && (
              <span className="text-caption text-gray-mid">{rows.length}</span>
            )}
          </div>
        </header>

        {items.isLoading ? (
          <p className="text-caption text-gray-mid italic m-0">Loading documents…</p>
        ) : items.isError ? (
          <p className="text-small text-status-error m-0">Could not load the requested documents.</p>
        ) : rows.length === 0 ? (
          <p className="text-caption text-gray-mid italic m-0">This request has no documents.</p>
        ) : (
          <ul className="divide-y divide-border-light border border-border-light rounded-md m-0 p-0 list-none">
            {rows.map((item) => {
              // WS-F — per-item action cluster. Archived rows offer Unarchive;
              // active rows offer Archive always + Delete only when untouched
              // (canHardDelete). Destructive Delete carries an inline confirm.
              const menuItems: OverflowMenuItem[] = item.isArchived
                ? [{ label: "Unarchive", onSelect: () => unarchiveItem.mutate(item.id) }]
                : [{ label: "Archive", onSelect: () => archiveItem.mutate(item.id) }];
              if (!item.isArchived && item.canHardDelete) {
                menuItems.push({
                  label: "Delete",
                  destructive: true,
                  confirm: "Delete this item? It can be recovered by an admin.",
                  onSelect: () => deleteItem.mutate(item.id),
                });
              }
              return (
                <li
                  key={item.id}
                  className={`flex items-center justify-between gap-space-3 p-space-3${
                    item.isArchived ? " opacity-60" : ""
                  }`}
                >
                  <div className="flex items-center gap-space-2 min-w-0">
                    <Badge status="info">{item.kind}</Badge>
                    <span className="text-small text-onyx truncate font-medium">
                      {item.templateName || `Item ${item.id.slice(0, 8)}`}
                    </span>
                    {item.isArchived && <Badge status="archived">Archived</Badge>}
                  </div>
                  <div className="flex items-center gap-space-2">
                    <ItemStatusBadge status={item.status} />
                    <OverflowMenu items={menuItems} label="Item actions" />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
